import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse, Plus, Trash2, ChevronLeft, Save, User, Phone, AlertTriangle, Pill, Syringe, Stethoscope, ShieldPlus, Loader2, Star } from "lucide-react";
import { BLOOD_TYPES, newEmergencyContact, newMedication, newProvider, newVaccination, emptyHealthCompartment } from "../../lib/vault/schemas";

/**
 * HealthCompartment — edits the encrypted health & emergency data compartment.
 * Everything is loaded into React state on mount, edited in memory, and saved
 * back through the vault service (which encrypts before hitting IndexedDB).
 */
export default function HealthCompartment({ vault, compartmentId, onBack }) {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("emergency");

  useEffect(() => {
    vault.load(compartmentId).then((d) => setData(d || emptyHealthCompartment())).catch(() => setData(emptyHealthCompartment()));
  }, [vault, compartmentId]);

  const set = (path, value) => {
    setData((d) => {
      const next = structuredClone(d);
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const updateArr = (arrKey, id, updater) => {
    setData((d) => {
      const next = structuredClone(d);
      next[arrKey] = next[arrKey].map((it) => it.id === id ? updater(it) : it);
      return next;
    });
    setDirty(true);
  };

  const addToArr = (arrKey, item) => {
    setData((d) => ({ ...d, [arrKey]: [...(d[arrKey] || []), item] }));
    setDirty(true);
  };
  const removeFromArr = (arrKey, id) => {
    setData((d) => ({ ...d, [arrKey]: d[arrKey].filter((it) => it.id !== id) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try { await vault.save(compartmentId, data); toast.success("Saved"); setDirty(false); }
    catch (e) { toast.error(e.message || "Could not save"); }
    setSaving(false);
  };

  if (!data) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>;

  const inp = "mt-1 w-full border border-slate-300 focus:border-emerald-500 px-3 py-2 text-sm rounded";

  const TABS = [
    { k: "emergency", l: "Emergency", icon: ShieldPlus },
    { k: "personal",  l: "Personal",  icon: User },
    { k: "meds",      l: "Meds",      icon: Pill },
    { k: "vaccines",  l: "Vaccines",  icon: Syringe },
    { k: "providers", l: "Providers", icon: Stethoscope },
  ];

  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="health-compartment">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
        <button onClick={save} disabled={!dirty || saving} data-testid="health-save"
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-700 rounded inline-flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="bg-gradient-to-br from-rose-50 to-white border border-rose-100 rounded p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-rose-600 grid place-items-center">
            <HeartPulse className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-serif font-900 text-2xl">Health &amp; Emergency</h1>
            <p className="text-[11px] text-slate-500">Encrypted on this device · never sent to any server</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-none">
        {TABS.map(({ k, l, icon: I }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`health-tab-${k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-700 rounded shrink-0 border ${tab === k ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-700"}`}>
            <I className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {tab === "emergency" && (
        <div className="space-y-4" data-testid="health-tab-emergency-content">
          <div className="border border-rose-200 bg-rose-50/50 rounded p-4">
            <h3 className="font-heading font-800 text-sm inline-flex items-center gap-1.5 text-rose-800 mb-3"><AlertTriangle className="w-4 h-4" /> Critical info for first responders</h3>
            <label className="block text-sm"><span className="font-600">Emergency instructions</span>
              <textarea rows={3} value={data.emergencyInstructions} onChange={(e) => set("emergencyInstructions", e.target.value)}
                data-testid="health-emergency-instructions" className={inp}
                placeholder="E.g. Diabetic — glucose gel in top pocket. Keep inhaler accessible." />
            </label>
            <div className="mt-3">
              <label className="block text-sm"><span className="font-600">Allergies</span>
                <TagInput value={data.allergies} onChange={(v) => set("allergies", v)} placeholder="Penicillin, peanuts…" testid="health-allergies" />
              </label>
            </div>
            <div className="mt-3">
              <label className="block text-sm"><span className="font-600">Medical conditions</span>
                <TagInput value={data.medicalConditions} onChange={(v) => set("medicalConditions", v)} placeholder="Asthma, Type 2 diabetes…" testid="health-conditions" />
              </label>
            </div>
          </div>

          <div className="border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-800 text-sm inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-emerald-600" /> Emergency contacts</h3>
              <button onClick={() => addToArr("emergencyContacts", newEmergencyContact())} data-testid="health-add-contact"
                className="text-xs font-700 text-emerald-700 inline-flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add contact</button>
            </div>
            <div className="space-y-3">
              {data.emergencyContacts.length === 0 && <p className="text-xs text-slate-500 italic">No contacts yet. Tap Add contact.</p>}
              {data.emergencyContacts.map((c) => (
                <div key={c.id} className={`border rounded p-3 ${c.isPrimary ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`health-contact-${c.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={c.name} onChange={(e) => updateArr("emergencyContacts", c.id, (x) => ({...x, name: e.target.value}))} placeholder="Name" className={inp} data-testid={`health-contact-${c.id}-name`} />
                    <input value={c.relationship} onChange={(e) => updateArr("emergencyContacts", c.id, (x) => ({...x, relationship: e.target.value}))} placeholder="Relationship" className={inp} />
                    <input value={c.phone} onChange={(e) => updateArr("emergencyContacts", c.id, (x) => ({...x, phone: e.target.value}))} placeholder="Phone" className={inp} data-testid={`health-contact-${c.id}-phone`} />
                    <input value={c.alternatePhone} onChange={(e) => updateArr("emergencyContacts", c.id, (x) => ({...x, alternatePhone: e.target.value}))} placeholder="Alt phone (optional)" className={inp} />
                    <input value={c.email} onChange={(e) => updateArr("emergencyContacts", c.id, (x) => ({...x, email: e.target.value}))} placeholder="Email (optional)" className="mt-1 col-span-2 w-full border border-slate-300 px-3 py-2 text-sm rounded" />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={c.isPrimary} onChange={(e) => {
                        // Only one primary at a time
                        setData((d) => {
                          const next = structuredClone(d);
                          next.emergencyContacts = next.emergencyContacts.map((it) => ({...it, isPrimary: it.id === c.id ? e.target.checked : (e.target.checked ? false : it.isPrimary)}));
                          return next;
                        });
                        setDirty(true);
                      }} />
                      <Star className={`w-3 h-3 ${c.isPrimary ? "text-amber-500 fill-amber-400" : "text-slate-400"}`} /> Primary contact
                    </label>
                    <button onClick={() => removeFromArr("emergencyContacts", c.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "personal" && (
        <div className="border border-slate-200 rounded p-4 space-y-3" data-testid="health-tab-personal-content">
          <label className="block text-sm"><span className="font-600">Full name</span>
            <input value={data.personalInfo.fullName} onChange={(e) => set("personalInfo.fullName", e.target.value)} className={inp} data-testid="health-personal-fullname" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Date of birth</span>
              <input type="date" value={data.personalInfo.dateOfBirth || ""} onChange={(e) => set("personalInfo.dateOfBirth", e.target.value)} className={inp} />
            </label>
            <label className="block text-sm"><span className="font-600">Blood type</span>
              <select value={data.personalInfo.bloodType || "Unknown"} onChange={(e) => set("personalInfo.bloodType", e.target.value)} className={inp}>
                {BLOOD_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Primary language</span>
              <input value={data.personalInfo.primaryLanguage || ""} onChange={(e) => set("personalInfo.primaryLanguage", e.target.value)} className={inp} placeholder="English" />
            </label>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input type="checkbox" checked={!!data.personalInfo.organDonor} onChange={(e) => set("personalInfo.organDonor", e.target.checked)} className="w-4 h-4" />
              <span>Organ donor</span>
            </label>
          </div>

          <div className="pt-3 border-t border-slate-100 space-y-2">
            <h4 className="font-heading font-800 text-xs uppercase tracking-wider text-slate-600">Insurance</h4>
            <input value={data.insurance.providerName || ""} onChange={(e) => set("insurance.providerName", e.target.value)} placeholder="Provider name" className={inp} />
            <input value={data.insurance.policyNumber || ""} onChange={(e) => set("insurance.policyNumber", e.target.value)} placeholder="Policy number" className={inp} />
            <input value={data.insurance.phone || ""} onChange={(e) => set("insurance.phone", e.target.value)} placeholder="Phone (24/7 line)" className={inp} />
          </div>
        </div>
      )}

      {tab === "meds" && (
        <ArraySection
          title="Medications" testid="health-meds"
          items={data.medications} onAdd={() => addToArr("medications", newMedication())} onRemove={(id) => removeFromArr("medications", id)}
          render={(m) => (
            <>
              <input value={m.name} onChange={(e) => updateArr("medications", m.id, (x) => ({...x, name: e.target.value}))} placeholder="Name" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <input value={m.dosage} onChange={(e) => updateArr("medications", m.id, (x) => ({...x, dosage: e.target.value}))} placeholder="Dosage" className={inp} />
                <input value={m.frequency} onChange={(e) => updateArr("medications", m.id, (x) => ({...x, frequency: e.target.value}))} placeholder="Frequency" className={inp} />
              </div>
              <input value={m.purpose || ""} onChange={(e) => updateArr("medications", m.id, (x) => ({...x, purpose: e.target.value}))} placeholder="Purpose (optional)" className={inp} />
            </>
          )}
        />
      )}

      {tab === "vaccines" && (
        <ArraySection
          title="Vaccinations" testid="health-vaccines"
          items={data.vaccinations} onAdd={() => addToArr("vaccinations", newVaccination())} onRemove={(id) => removeFromArr("vaccinations", id)}
          render={(v) => (
            <>
              <input value={v.vaccineName} onChange={(e) => updateArr("vaccinations", v.id, (x) => ({...x, vaccineName: e.target.value}))} placeholder="Vaccine (e.g. Yellow Fever)" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={v.dateAdministered} onChange={(e) => updateArr("vaccinations", v.id, (x) => ({...x, dateAdministered: e.target.value}))} className={inp} />
                <input type="date" value={v.expirationDate || ""} onChange={(e) => updateArr("vaccinations", v.id, (x) => ({...x, expirationDate: e.target.value}))} className={inp} placeholder="Expiry" />
              </div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!v.isTravelRequired} onChange={(e) => updateArr("vaccinations", v.id, (x) => ({...x, isTravelRequired: e.target.checked}))} className="w-4 h-4" /> Required for travel</label>
            </>
          )}
        />
      )}

      {tab === "providers" && (
        <ArraySection
          title="Providers" testid="health-providers"
          items={data.providers} onAdd={() => addToArr("providers", newProvider())} onRemove={(id) => removeFromArr("providers", id)}
          render={(p) => (
            <>
              <input value={p.name} onChange={(e) => updateArr("providers", p.id, (x) => ({...x, name: e.target.value}))} placeholder="Doctor's name" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <input value={p.specialty} onChange={(e) => updateArr("providers", p.id, (x) => ({...x, specialty: e.target.value}))} placeholder="Specialty" className={inp} />
                <input value={p.phone} onChange={(e) => updateArr("providers", p.id, (x) => ({...x, phone: e.target.value}))} placeholder="Phone" className={inp} />
              </div>
              <input value={p.clinicName || ""} onChange={(e) => updateArr("providers", p.id, (x) => ({...x, clinicName: e.target.value}))} placeholder="Clinic (optional)" className={inp} />
            </>
          )}
        />
      )}

      <div className="mt-4 text-[10px] text-slate-500 flex items-center gap-1">
        <ShieldPlus className="w-3 h-3" /> Everything here is encrypted with AES-GCM before it touches disk. Auto-locks after 5 min idle.
      </div>
    </div>
  );
}

function ArraySection({ title, items, onAdd, onRemove, render, testid }) {
  return (
    <div className="border border-slate-200 rounded p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-800 text-sm">{title} · {items.length}</h3>
        <button onClick={onAdd} data-testid={`${testid}-add`} className="text-xs font-700 text-emerald-700 inline-flex items-center gap-1 hover:underline">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-xs text-slate-500 italic">None yet. Tap Add.</p>}
        {items.map((item) => (
          <div key={item.id} className="border border-slate-200 rounded p-3 space-y-2" data-testid={`${testid}-row-${item.id}`}>
            {render(item)}
            <div className="text-right">
              <button onClick={() => onRemove(item.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TagInput({ value, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    if (value.includes(t)) return setText("");
    onChange([...(value || []), t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-1 flex-wrap mt-1 mb-2">
        {(value || []).map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 border border-rose-300 text-rose-900 text-xs rounded" data-testid={`${testid}-chip-${t}`}>
            {t}
            <button onClick={() => onChange(value.filter((x) => x !== t))} className="text-rose-500 hover:text-rose-800">×</button>
          </span>
        ))}
      </div>
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
        placeholder={placeholder} data-testid={testid}
        className="w-full border border-slate-300 px-3 py-2 text-sm rounded" />
    </div>
  );
}
